import React from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    ArcElement,
    Tooltip,
    Legend,
    Title,
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    ArcElement,
    Tooltip,
    Legend,
    Title
);

// Degradé vínico para las tortas: anclas de rojo vivo a bordó oscuro.
// Para N porciones se interpolan N tonos — no hace falta definir un color
// por variable, funciona con cualquier cantidad.
const ANCLAS_VINO = ['#e53835', '#94172e', '#551933'];

const hexARgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const rgbAHex = (rgb) =>
    '#' + rgb.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('');

// Color en la posición t del degradé (0 = rojo vivo, 1 = bordó oscuro).
const colorVinico = (t) => {
    const segmentos = ANCLAS_VINO.length - 1;
    const seg = Math.min(Math.floor(t * segmentos), segmentos - 1);
    const local = t * segmentos - seg;
    const a = hexARgb(ANCLAS_VINO[seg]);
    const b = hexARgb(ANCLAS_VINO[seg + 1]);
    return rgbAHex(a.map((c, i) => c + (b[i] - c) * local));
};

// Colores por ranking de valor (no por posición): la porción más grande
// siempre recibe el rojo, la más chica el bordó oscuro, sin importar el
// orden en que el modelo haya emitido los datos.
const coloresPorValor = (valores) => {
    const ranking = valores.map((_, i) => i).sort((a, b) => valores[b] - valores[a]);
    const colores = new Array(valores.length);
    const n = valores.length;
    ranking.forEach((idxOriginal, rank) => {
        colores[idxOriginal] = colorVinico(n === 1 ? 0 : rank / (n - 1));
    });
    return colores;
};

const formatoMonto = (valor) =>
    new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(valor);

/**
 * Renderiza un bloque ```chart emitido por Druida como gráfico interactivo.
 * Espera: {"tipo": "barras"|"lineas"|"torta", "titulo": "...", "datos": [{"label", "valor"}]}
 * Si el JSON está incompleto (llega por streaming) o malformado, muestra un
 * placeholder discreto en vez de romper o mostrar JSON crudo.
 */
const ChartBlock = ({ payload }) => {
    let spec = null;
    try {
        spec = JSON.parse(payload);
    } catch {
        spec = null;
    }

    const datos = Array.isArray(spec?.datos)
        ? spec.datos.filter((d) => d && d.label != null && !isNaN(Number(d.valor)))
        : [];

    if (!datos.length) {
        return (
            <div style={{ padding: '12px', color: '#888', fontSize: '14px', fontStyle: 'italic' }}>
                Preparando gráfico…
            </div>
        );
    }

    const labels = datos.map((d) => String(d.label));
    const valores = datos.map((d) => Number(d.valor));
    const tipo = spec.tipo || 'barras';
    const esTorta = tipo === 'torta';
    const esHorizontal = tipo === 'barras_h';

    const data = {
        labels,
        datasets: [
            {
                label: spec.titulo || '',
                data: valores,
                backgroundColor: esTorta
                    ? coloresPorValor(valores)
                    : 'rgba(229, 57, 53, 0.75)',
                borderColor: esTorta ? undefined : '#e53935',
                borderWidth: esTorta ? 0 : 1,
                tension: 0,
                fill: false,
                pointBackgroundColor: '#e53935',
            },
        ],
    };

    // En barras_h el eje de valores es X y el de categorías es Y.
    const ejeValores = {
        beginAtZero: true,
        ticks: { callback: (v) => formatoMonto(v) },
        grid: { display: false },
        border: { display: true },
    };
    const ejeCategorias = {
        grid: { display: false },
        border: { display: true },
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: esHorizontal ? 'y' : 'x',
        plugins: {
            legend: { display: esTorta, position: 'bottom' },
            title: {
                display: Boolean(spec.titulo),
                text: spec.titulo,
                font: { size: 14 },
                color: '#4d4d4d',
            },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const v = esTorta
                            ? ctx.parsed
                            : esHorizontal
                              ? ctx.parsed.x
                              : ctx.parsed.y;
                        return ` ${formatoMonto(v)}`;
                    },
                },
            },
        },
        scales: esTorta
            ? undefined
            : esHorizontal
              ? { x: ejeValores, y: ejeCategorias }
              : { x: ejeCategorias, y: ejeValores },
    };

    const Componente = tipo === 'lineas' ? Line : esTorta ? Pie : Bar;

    // Las barras horizontales crecen con la cantidad de ítems para que un
    // top 10 no quede aplastado.
    const altura = esTorta
        ? 260
        : esHorizontal
          ? Math.min(420, Math.max(220, datos.length * 40 + 60))
          : 280;

    return (
        <div style={{ height: `${altura}px`, margin: '10px 0', position: 'relative' }}>
            <Componente data={data} options={options} />
        </div>
    );
};

export default ChartBlock;
